import { useEffect, useRef } from "react";

import { API_URL, refreshSession } from "./client";
import { tokens } from "./tokens";

/** What a screen is told: the kind of row that changed, and where. */
export interface LiveChange {
  /** `appointments`, `waitlist_entries`, `booking_requests` — or `*` after a reconnect. */
  kind: string;
  branchId: string | null;
}

const EVENT = "dentacore:live";
const RETRY_MS = 3_000;
/** A server that keeps refusing is asked less and less often, up to this. */
const MAX_RETRY_MS = 60_000;

/** The running stream, or null: one per tab. */
let current: { stop: () => void } | null = null;

/**
 * **Live updates** (OQ-D19, STORY-043): one stream per tab, read with `fetch`
 * so the bearer token goes in a header, never a URL. Each change is broadcast
 * as a window event; screens listen through `useLiveRefresh`. When the stream
 * drops it reconnects and tells every screen to refetch (`*`), since anything
 * may have changed while it was away. Safe to call more than once.
 *
 * **Returns the stop**, which the shell runs when it unmounts — on sign-out, so
 * a signed-out tab holds no stream and no retry timer (QA 2026-11-01).
 *
 * **A 401 refreshes the session, then reconnects** with the new token. It used
 * to retry the stale one every three seconds for good: the server ends the
 * stream when its token expires, and after any API restart an idle tab kept
 * knocking with an expired token, and its screens stopped updating, until the
 * person did something that refreshed it. A session that cannot be refreshed
 * is over, so the stream stops rather than knocking on.
 */
export function startLiveUpdates(): () => void {
  // Off under the end-to-end suite, whose waits need the network to go idle.
  if (import.meta.env.VITE_LIVE_UPDATES === "off") return () => undefined;
  if (current !== null) return current.stop;

  const abort = new AbortController();
  let timer = 0;
  let failures = 0;
  const stop = (): void => {
    abort.abort();
    window.clearTimeout(timer);
    if (current?.stop === stop) current = null;
  };
  const later = (reconnecting: boolean): void => {
    if (abort.signal.aborted) return;
    const wait = Math.min(RETRY_MS * 2 ** Math.min(failures, 5), MAX_RETRY_MS);
    timer = window.setTimeout(() => void connect(reconnecting), wait);
  };

  const connect = async (reconnecting: boolean, fresh = false): Promise<void> => {
    const token = tokens.getAccessToken();
    if (token === null) {
      // Not signed in yet, or a refresh in flight: try again shortly.
      timer = window.setTimeout(() => void connect(reconnecting), RETRY_MS);
      return;
    }
    try {
      const response = await fetch(`${API_URL}/live/stream`, {
        headers: { authorization: `Bearer ${token}`, accept: "text/event-stream" },
        credentials: "include",
        signal: abort.signal,
      });
      // A 401 on a token just refreshed is not an expiry; it waits like any
      // other refusal, so the two can never chase each other in a tight loop.
      if (response.status === 401 && !fresh) {
        try {
          await refreshSession();
        } catch {
          stop();
          return;
        }
        // A fresh token: straight back, no wait — this is the expected path
        // every time the server ends a stream at its token's expiry.
        void connect(true, true);
        return;
      }
      if (!response.ok || response.body === null)
        throw new Error(`live ${String(response.status)}`);
      failures = 0;
      if (reconnecting) broadcast({ kind: "*", branchId: null });
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let gap = buffer.indexOf("\n\n");
        while (gap !== -1) {
          handle(buffer.slice(0, gap));
          buffer = buffer.slice(gap + 2);
          gap = buffer.indexOf("\n\n");
        }
      }
    } catch {
      // Dropped, refused or offline: the next attempt says so by refetching.
      failures += 1;
    }
    later(true);
  };

  current = { stop };
  void connect(false);
  return stop;
}

function handle(block: string): void {
  let type = "message";
  const data: string[] = [];
  for (const line of block.split("\n")) {
    if (line.startsWith("event:")) type = line.slice(6).trim();
    else if (line.startsWith("data:")) data.push(line.slice(5).trim());
  }
  if (type !== "change" || data.length === 0) return;
  try {
    broadcast(JSON.parse(data.join("\n")) as LiveChange);
  } catch {
    // Not one of ours.
  }
}

function broadcast(change: LiveChange): void {
  window.dispatchEvent(new CustomEvent<LiveChange>(EVENT, { detail: change }));
}

/**
 * **Refetch when something this screen shows changes** (OQ-D19). `kinds` are
 * the tables it reads; `branchIds`, when given, the locations it shows. Bursts
 * are folded into one refetch.
 */
export function useLiveRefresh(
  kinds: readonly string[],
  reload: () => void,
  branchIds?: readonly string[],
): void {
  const latest = useRef(reload);
  latest.current = reload;
  const kindKey = kinds.join(",");
  const branchKey = branchIds?.join(",") ?? "";
  useEffect(() => {
    let timer = 0;
    const listener = (event: Event): void => {
      const change = (event as CustomEvent<LiveChange>).detail;
      const wanted =
        change.kind === "*" ||
        (kindKey.split(",").includes(change.kind) &&
          (branchKey === "" ||
            change.branchId === null ||
            branchKey.split(",").includes(change.branchId)));
      if (!wanted) return;
      window.clearTimeout(timer);
      timer = window.setTimeout(() => latest.current(), 300);
    };
    window.addEventListener(EVENT, listener);
    return () => {
      window.removeEventListener(EVENT, listener);
      window.clearTimeout(timer);
    };
  }, [kindKey, branchKey]);
}
